import React, { useState, useEffect } from 'react';
import { FileText, Edit3, Check, AlertCircle, Info } from 'lucide-react';
import FormattedText from './FormattedText';

/**
 * ReportEditorProfessional - Editor profissional para laudos audiométricos
 * 
 * Interface visual amigável para médicos/fonoaudiólogos editarem laudos,
 * sem precisar ver JSON cru.
 * 
 * @param {Object} reportData - JSON string ou objeto do laudo
 * @param {String} audiometryType - Tipo de audiometria ('simple' ou 'nexo-causal')
 * @param {Function} onSave - Callback quando salvar (recebe objeto editado)
 */
const ReportEditorProfessional = ({ reportData, audiometryType = 'simple', onSave }) => {
  const [data, setData] = useState({});
  const [editMode, setEditMode] = useState({
    nr7: false,
    portaria: false,
    laudo: false
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Parse reportData quando mudar
  useEffect(() => {
    try {
      const parsed = typeof reportData === 'string' 
        ? JSON.parse(reportData) 
        : reportData;
      setData(parsed || {});
    } catch (err) {
      console.error('Erro ao parsear reportData:', err);
      setData({});
    }
  }, [reportData]);

  // Auto-save quando data mudar (com debounce)
  useEffect(() => {
    if (hasChanges && onSave) {
      const timer = setTimeout(() => {
        onSave(data);
        setHasChanges(false);
      }, 500); // Debounce de 500ms

      return () => clearTimeout(timer);
    }
  }, [data, hasChanges, onSave]);

  // Toggle edit mode para uma seção
  const toggleEdit = (section) => {
    setEditMode(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Atualizar campo no data
  const updateField = (path, value) => {
    setHasChanges(true);
    setData(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  // Helpers para acessar dados nested
  const get = (path, defaultValue = '') => {
    const keys = path.split('.');
    let current = data;
    for (let key of keys) {
      if (current && current[key] !== undefined) {
        current = current[key];
      } else {
        return defaultValue;
      }
    }
    return current;
  };

  // Helper para converter valor em string formatada para FormattedText
  const getAsString = (path, defaultValue = '') => {
    const value = get(path, defaultValue);
    
    // Se já é string, retorna direto
    if (typeof value === 'string') {
      return value;
    }
    
    // Se é array, processa cada item
    if (Array.isArray(value)) {
      return value.map((item, index) => {
        // Se item é string, retorna com bullet point
        if (typeof item === 'string') {
          return `• ${item}`;
        }
        // Se item é objeto, formata recursivamente
        if (typeof item === 'object' && item !== null) {
          return formatObject(item, 1);
        }
        return String(item);
      }).join('\n');
    }
    
    // Se é objeto, formata de forma legível
    if (typeof value === 'object' && value !== null) {
      return formatObject(value, 0);
    }
    
    // Fallback geral
    return String(value || defaultValue);
  };

  // Helper recursivo para formatar objetos de forma legível
  const formatObject = (obj, indentLevel = 0) => {
    const indent = '  '.repeat(indentLevel);
    const parts = [];

    Object.entries(obj).forEach(([key, val]) => {
      // Converte snake_case para Título Legível
      const label = key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      if (val === null || val === undefined) {
        // Ignora valores nulos
        return;
      } else if (Array.isArray(val)) {
        // Arrays: lista com bullets
        if (val.length === 0) return; // Ignora arrays vazios
        
        const items = val.map(item => {
          if (typeof item === 'string') {
            return `${indent}  • ${item}`;
          } else if (typeof item === 'object' && item !== null) {
            // Objetos dentro de array: formata inline se simples
            const entries = Object.entries(item);
            if (entries.length <= 3) {
              return `${indent}  • ${entries.map(([k, v]) => `${k}: ${v}`).join(', ')}`;
            }
            return `${indent}  • ${formatObject(item, indentLevel + 2)}`;
          }
          return `${indent}  • ${item}`;
        }).join('\n');
        
        parts.push(`${indent}• ${label}:\n${items}`);
      } else if (typeof val === 'object' && val !== null) {
        // Objetos aninhados: formata recursivamente com bullet
        const nestedContent = formatObject(val, indentLevel + 1);
        parts.push(`${indent}• ${label}:\n${nestedContent}`);
      } else if (typeof val === 'boolean') {
        // Booleanos: Sim/Não com bullet
        parts.push(`${indent}• ${label}: ${val ? 'Sim' : 'Não'}`);
      } else if (typeof val === 'string' && val.length > 80) {
        // Strings longas: quebra em múltiplas linhas com bullet
        parts.push(`${indent}• ${label}:\n${indent}  ${val}`);
      } else {
        // Valores simples: inline com bullet
        parts.push(`${indent}• ${label}: ${val}`);
      }
    });

    return parts.join('\n');
  };

  // Renderizar badge colorido baseado no valor
  const renderBadge = (value, trueColor = 'green', falseColor = 'red') => {
    const isTrue = value === true || value === 'sim' || value === 'Sim';
    const color = isTrue ? trueColor : falseColor;
    const text = isTrue ? '✓ Sim' : '✗ Não';
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-800`}>
        {text}
      </span>
    );
  };

  // Renderizar campo de select
  const renderSelect = (path, options, label, disabled = false) => {
    return (
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        <select
          value={get(path, '')}
          onChange={(e) => updateField(path, e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
        >
          <option value="">Selecione...</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-blue-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Laudo Audiométrico Ocupacional
            </h2>
            <p className="text-sm text-gray-500">
              Edite as informações clínicas abaixo conforme necessário
            </p>
          </div>
        </div>
        {hasChanges && (
          <div className="flex items-center gap-2 text-orange-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Alterações não salvas</span>
          </div>
        )}
      </div>

      {/* Seção 1: Validação NR-7 */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span className="text-2xl">✓</span>
            Validação NR-7
          </h3>
          <button
            onClick={() => toggleEdit('nr7')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            {editMode.nr7 ? <Check className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
            {editMode.nr7 ? 'Concluir' : 'Editar'}
          </button>
        </div>

        {!editMode.nr7 ? (
          // Modo visualização
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Repouso auditivo (14h):</span>
              {renderBadge(get('validacao_nr7.repouso_14h_ok'), 'green', 'red')}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Equipamento registrado:</span>
              {renderBadge(get('validacao_nr7.equipamento_registrado'), 'green', 'orange')}
            </div>
            {get('validacao_nr7.observacoes') && (
              <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                <p className="text-xs text-gray-600 font-medium mb-1">Observações:</p>
                <p className="text-gray-700">{get('validacao_nr7.observacoes')}</p>
              </div>
            )}
          </div>
        ) : (
          // Modo edição
          <div className="space-y-3">
            {renderSelect('validacao_nr7.repouso_14h_ok', [
              { value: true, label: '✓ Sim' },
              { value: false, label: '✗ Não' }
            ], 'Repouso auditivo (14h)')}
            
            {renderSelect('validacao_nr7.equipamento_registrado', [
              { value: true, label: '✓ Sim' },
              { value: false, label: '✗ Não' }
            ], 'Equipamento registrado')}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observações
              </label>
              <textarea
                value={get('validacao_nr7.observacoes', '')}
                onChange={(e) => updateField('validacao_nr7.observacoes', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Dados de repouso não informados pelo paciente"
              />
            </div>
          </div>
        )}
      </div>

      {/* Seção 2: Interpretação Portaria 19 */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span className="text-2xl">⚠️</span>
            Interpretação Audiométrica (Portaria 19)
          </h3>
          <button
            onClick={() => toggleEdit('portaria')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            {editMode.portaria ? <Check className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
            {editMode.portaria ? 'Concluir' : 'Editar'}
          </button>
        </div>

        {!editMode.portaria ? (
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Classificação:</span>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {get('interpretacao_portaria19.exames[0].classificacao', 'N/A')}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <p className="text-xs text-gray-500 font-medium">Média 3-6kHz OD:</p>
                <p className="text-gray-700">{get('interpretacao_portaria19.exames[0].od_media_3a6khz_db', 'N/A')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Média 3-6kHz OE:</p>
                <p className="text-gray-700">{get('interpretacao_portaria19.exames[0].oe_media_3a6khz_db', 'N/A')}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {renderSelect('interpretacao_portaria19.exame_a1', [
              { value: 'limites_<=25dB|nao_sugestivo', label: 'Limites normais (≤25 dB) - Não sugestivo de PAIRO' },
              { value: 'pairo_leve', label: 'PAIRO Leve' },
              { value: 'pairo_moderado', label: 'PAIRO Moderado' },
              { value: 'pairo_severo', label: 'PAIRO Severo' }
            ], 'Classificação do Exame')}
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Média 3-6kHz OD (dB)
                </label>
                <input
                  type="number"
                  value={get('interpretacao_portaria19.media_3_6_od', '')}
                  onChange={(e) => updateField('interpretacao_portaria19.media_3_6_od', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 35"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Média 3-6kHz OE (dB)
                </label>
                <input
                  type="number"
                  value={get('interpretacao_portaria19.media_3_6_oe', '')}
                  onChange={(e) => updateField('interpretacao_portaria19.media_3_6_oe', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 40"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Seção 3: Laudo Clínico Narrativo */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span className="text-2xl">🔬</span>
            Laudo Clínico - Texto Narrativo
          </h3>
          <button
            onClick={() => toggleEdit('laudo')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            {editMode.laudo ? <Check className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
            {editMode.laudo ? 'Concluir' : 'Editar'}
          </button>
        </div>

        {!editMode.laudo ? (
          <div className="space-y-4">
            {/* Interpretação Técnica */}
            {get('laudo_narrativo.interpretacao_tecnica') && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700 font-semibold mb-3 flex items-center gap-2">
                  🔬 Interpretação Técnica
                </p>
                <FormattedText text={getAsString('laudo_narrativo.interpretacao_tecnica')} />
              </div>
            )}

            {/* Conclusão Ocupacional */}
            {get('laudo_narrativo.conclusao_ocupacional') && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 font-semibold mb-3 flex items-center gap-2">
                  💼 Conclusão Ocupacional
                </p>
                <FormattedText text={getAsString('laudo_narrativo.conclusao_ocupacional')} />
              </div>
            )}

            {/* Conclusão Final */}
            {get('laudo_narrativo.conclusao_final') && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-700 font-semibold mb-3 flex items-center gap-2">
                  ✅ Conclusão Final
                </p>
                <FormattedText text={getAsString('laudo_narrativo.conclusao_final')} />
              </div>
            )}

            {/* Recomendações */}
            {get('laudo_narrativo.recomendacoes') && (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-700 font-semibold mb-3 flex items-center gap-2">
                  � Recomendações
                </p>
                <FormattedText text={getAsString('laudo_narrativo.recomendacoes')} />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                🔬 Interpretação Técnica
              </label>
              <textarea
                value={get('laudo_narrativo.interpretacao_tecnica', '')}
                onChange={(e) => updateField('laudo_narrativo.interpretacao_tecnica', e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Descreva os achados audiométricos: tipo de perda, grau, configuração, limiares..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                💼 Conclusão Ocupacional
              </label>
              <textarea
                value={get('laudo_narrativo.conclusao_ocupacional', '')}
                onChange={(e) => updateField('laudo_narrativo.conclusao_ocupacional', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Conclusão sobre adequação para a função, classificação segundo critérios ocupacionais..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                ✅ Conclusão Final
              </label>
              <textarea
                value={get('laudo_narrativo.conclusao_final', '')}
                onChange={(e) => updateField('laudo_narrativo.conclusao_final', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Conclusão final resumida do laudo..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                💡 Recomendações
              </label>
              <textarea
                value={get('laudo_narrativo.recomendacoes', '')}
                onChange={(e) => updateField('laudo_narrativo.recomendacoes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Recomendações clínicas e administrativas..."
              />
            </div>
          </div>
        )}
      </div>

      {/* Alertas de Dados Faltantes */}
      {!get('laudo_narrativo.interpretacao_tecnica') && (
        <div className="bg-orange-50 border border-orange-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-orange-800 mb-1">
                Atenção: Informações faltantes
              </h4>
              <ul className="text-sm text-orange-700 space-y-1">
                <li>• Interpretação técnica não preenchida</li>
              </ul>
              <p className="text-xs text-orange-600 mt-2">
                Complete estas informações para gerar um laudo completo.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportEditorProfessional;
